import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DashboardConfig } from '@/features/dashboard/config';
import { Brand } from '@/features/survey/components/Brand';
import { ApproveDialog } from '@/features/survey/components/ApproveDialog';
import { LangToggle } from '@/features/survey/components/LangToggle';
import { SurveyI18nProvider } from '@/features/survey/components/SurveyI18nProvider';
import { useSurveyI18n } from '@/features/survey/i18n';
import { downloadCsv } from '@/features/survey/csv';
import { fmtDate, fmtInt } from '@/features/survey/utils';
import {
  approveSurveyWinner,
  fmtCardNumber,
  getSurveyWinners,
  isMissing,
  roundLabel,
  SU_WINNERS_PREVIEW,
  winnersCsv,
  type SurveyWinner,
} from '@/features/survey/winners';
import '@/features/survey/survey.css';

/*
 * Winners & prizes — a page beside the Système U dashboard. One row per weekly
 * draw; a winner the client has not completed yet reads "Missing information"
 * and carries the "Provide information and approve" action.
 *
 * Until the endpoint exists, DEV shows the design's four draws and approves them
 * in local state only (with a "Sample data" pill). A deployed build shows the
 * error instead — never invented winners. English or French, like the dashboard.
 */
export function SurveyWinnersPage({ config }: { config: DashboardConfig }) {
  return (
    <SurveyI18nProvider>
      <SurveyWinners config={config} />
    </SurveyI18nProvider>
  );
}

function SurveyWinners({ config }: { config: DashboardConfig }) {
  const { lang, t } = useSurveyI18n();
  const queryClient = useQueryClient();
  const queryKey = ['survey-winners', config.apiBaseUrl, config.reportId];
  const winners = useQuery({ queryKey, queryFn: () => getSurveyWinners(config.reportId, config.apiBaseUrl), retry: false });

  const preview = winners.isError && import.meta.env.DEV;
  const [previewRows, setPreviewRows] = useState<SurveyWinner[]>(SU_WINNERS_PREVIEW);
  const rows = preview ? previewRows : (winners.data ?? []);

  const [editing, setEditing] = useState<SurveyWinner | null>(null);
  const approve = useMutation({
    mutationFn: async (v: { winner: SurveyWinner; storeAddress: string; displayName: string }) => {
      if (preview) {
        setPreviewRows((cur) =>
          cur.map((w) => (w.id === v.winner.id ? { ...w, storeAddress: v.storeAddress, displayName: v.displayName, status: 'approved' } : w)),
        );
        return;
      }
      await approveSurveyWinner(config.reportId, config.apiBaseUrl, v.winner.id, { storeAddress: v.storeAddress, displayName: v.displayName });
    },
    onSuccess: async () => {
      setEditing(null);
      if (!preview) await queryClient.invalidateQueries({ queryKey });
    },
  });

  return (
    <div className="su-root" lang={lang}>
      <header className="su-header">
        <div className="su-wrap su-header-top" style={{ paddingTop: 32, paddingBottom: 32, alignItems: 'center' }}>
          <Brand title="Système U — Quiz — Bien manger" subtitle={t.winnersSubtitle} />
          <div className="su-controls">
            {preview && <span className="su-preview-pill">{t.sampleData}</span>}
            <LangToggle />
            <Link className="su-ghost" to={config.path}>
              {t.backToDashboard}
            </Link>
          </div>
        </div>
      </header>

      <main className="su-wrap su-main">
        <section className="su-card" style={{ gap: 24 }}>
          <div className="su-section-head">
            <div className="su-stack" style={{ gap: 4 }}>
              <h2>{t.winnersTitle}</h2>
              <span className="su-note">{t.winnersNote}</span>
            </div>
            <button
              type="button"
              className="su-secondary"
              disabled={rows.length === 0}
              onClick={() => downloadCsv('systeme-u-quiz-winners.csv', winnersCsv(rows, t))}
            >
              {t.exportCsv}
            </button>
          </div>

          {winners.isError && !preview && <div className="su-error">{t.winnersLoadError}</div>}
          {winners.isPending && <p className="su-note">{t.loadingWinners}</p>}
          {!winners.isPending && rows.length === 0 && !winners.isError && <p className="su-note">{t.noDrawYet}</p>}

          {rows.length > 0 && (
            <div className="su-scroll">
              <table className="su-table winners">
                <thead>
                  <tr>
                    <th>{t.winnersCol.weekDate}</th>
                    <th>{t.winnersCol.store}</th>
                    <th>{t.winnersCol.name}</th>
                    <th>{t.winnersCol.card}</th>
                    <th>{t.winnersCol.prize}</th>
                    <th>{t.winnersCol.status}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((w) => (
                    <tr key={w.id}>
                      <td>
                        <span className="su-wk">{roundLabel(w, t)}</span>
                        {fmtDate(w.drawnAt)}
                      </td>
                      <td>{isMissing(w) ? <span className="su-missing">{t.missingInfo}</span> : (w.storeAddress ?? '—')}</td>
                      <td>{isMissing(w) ? <span className="su-missing">{t.missingInfo}</span> : (w.displayName ?? '—')}</td>
                      <td className="su-card-number">{fmtCardNumber(w.cardNumber)}</td>
                      <td>
                        <span className="su-prize">{t.prizeCredit(fmtInt(w.prizeEur))}</span>
                      </td>
                      <td>
                        {w.status === 'approved' ? (
                          <span className="su-approved">
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="3"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                            >
                              <path d="M5 12l5 5L20 7" />
                            </svg>
                            {t.approved}
                          </span>
                        ) : w.status === 'no_winner' ? (
                          <span className="su-note">{t.noWinner}</span>
                        ) : (
                          <button type="button" className="su-primary" onClick={() => setEditing(w)}>
                            {t.provideAndApprove}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>

      {editing && (
        <ApproveDialog
          winner={editing}
          saving={approve.isPending}
          serverError={approve.isError ? t.saveError : null}
          onCancel={() => {
            setEditing(null);
            approve.reset();
          }}
          onSave={(body) => approve.mutate({ winner: editing, ...body })}
        />
      )}
    </div>
  );
}
